import { FormState } from 'final-form'
import { useCreateTowService } from 'hooks/aaa'
import { useCreateUlyTowService } from 'hooks/urgent.ly'
import { EventTypes } from 'types/events'
import { useCreateUniversalService } from '../universalPayload/useCreateUniversalService'
import { FormattedCaller } from 'components/fields/Submit'
import { ProviderAllocation } from 'types/dispatchConfig'
import { ProviderCount } from '../universalPayload/useGetProviderCounts'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useDispatchConfig } from 'hooks/universalPayload'
import { ProviderNames, getNextProvider, getSecondaryProvider } from './utils'

type CreateTow = (state: FormState<any>, caller: FormattedCaller) => Promise<any>
type CreateUniversal = (state: FormState<any>, caller: FormattedCaller, provider: ProviderNames) => Promise<any>

function createTowDispatch({
  createAAATowService,
  createULYTowService,
  createUniversalService,
}: {
  createAAATowService: CreateTow
  createULYTowService: CreateTow
  createUniversalService: CreateUniversal
}) {
  return async (state: FormState<any>, caller: FormattedCaller, providerName: String, stateConfig: ProviderAllocation[]) => {
    const { towDirection, rideAlong, scheduledAt } = state.values
    // STM for Tow Exchange is:
    // rideAlong = false AND towDirection = disabledVehicle
    // and for No Exchange it is:
    // rideAlong = false AND towDirection = undefined
    const isSTM =
      rideAlong !== undefined &&
      rideAlong === false &&
      (towDirection === undefined || (towDirection !== undefined && towDirection === 'disabledVehicle'))

    const secondaryProviderName = getSecondaryProvider(stateConfig)

    if (providerName === ProviderNames.AAA) {
      if (!isSTM && !scheduledAt) return await createAAATowService(state, caller)
      else {
        if (secondaryProviderName === ProviderNames.ALLSTATE) {
          return await createUniversalService(state, caller, ProviderNames.ALLSTATE)
        } else {
          return await createULYTowService(state, caller)
        }
      }
    }
    if (providerName === ProviderNames.URGENTLY) {
      return await createULYTowService(state, caller)
    }
    return await createUniversalService(state, caller, ProviderNames.ALLSTATE)
  }
}

export const useCreateTowDispatchService = (eventType: EventTypes, providerCount: ProviderCount) => {
  const { roadsideForceDispatchProvider } = useFlags()
  const getDispatchConfig = useDispatchConfig()
  const createAAATowService = useCreateTowService(eventType)
  const createULYTowService = useCreateUlyTowService(eventType)
  const createUniversalService = useCreateUniversalService(eventType)

  const towDispatch = createTowDispatch({
    createAAATowService,
    createULYTowService,
    createUniversalService,
  })

  return async (state: FormState<any>, caller: FormattedCaller) => {
    // Check LaunchDarkly flag value
    switch (roadsideForceDispatchProvider) {
      case 'uly': // Force ULY provider
        return await createULYTowService(state, caller)
      case 'aaa': // Force AAA provider
        return await createAAATowService(state, caller)
      case 'allstate': // Force Allstate provider
        return await createUniversalService(state, caller, ProviderNames.ALLSTATE)
      case 'default':
      default:
        // Fallback to original logic when flag is "default"
        break
    }

    // Ratio/config-based routing
    const { serviceLocations } = state.values
    const config = await getDispatchConfig()
    const vehicleState =
      serviceLocations?.vehicleLocation?.address?.state || serviceLocations?.callerLocation?.address?.state || 'N/A'
    const thisStateConfig =
      (config[vehicleState as keyof typeof config] as ProviderAllocation[]) ||
      (config['default' as keyof typeof config] as ProviderAllocation[])
    const providerName = getNextProvider(thisStateConfig, providerCount)

    return await towDispatch(state, caller, providerName, thisStateConfig)
  }
}
